import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { NotificationService } from '../../../../services/notification.service';
import { DamageManagementService } from '../../../../services/damage-management.service';
import { Damage } from '../../../../interfaces/Damages.interfaces';

/**
 * Creazione di una nuova pratica danno per un collo.
 *
 * Raggiunto da /damaged-parcels/create/{parcelCode} (bottone "Apri Pratica
 * Danno" della tabella colli). Il form copre tutti i campi editabili di
 * Damage (interfaccia su Opr_Damages_DTO); restano fuori solo i campi
 * generati dal server: damageId, damageCode, audit (created/updated) e
 * damageVersion. Stile allineato a damage-management: input con icona
 * (filter-input-wrapper) e date con flatpickr.
 */
@Component({
  selector: 'app-create-damage',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './create-damage.component.html',
  styleUrl: './create-damage.component.css'
})
export class CreateDamageComponent implements OnInit, AfterViewInit, OnDestroy {

  form!: FormGroup;
  parcelCode = '';
  isSaving = false;

  // Opzioni dei campi a scelta (valori usati dal dominio danni)
  statusOptions        = ['OPEN', 'IN_PROGRESS', 'WAITING', 'CLOSED', 'REJECTED'];
  typeOptions          = ['PHYSICAL', 'WATER', 'BREAKAGE', 'MISSING_PARTS', 'PACKAGING', 'OTHER'];
  severityOptions      = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  detectedPhaseOptions = ['LOADING', 'UNLOADING', 'TRANSPORT', 'WAREHOUSE', 'DELIVERY'];
  sourceOptions        = ['OPERATOR', 'CARRIER', 'CUSTOMER', 'DEALER', 'SYSTEM'];
  liablePartyOptions   = ['CARRIER', 'WAREHOUSE', 'CLIENT', 'DEALER', 'UNKNOWN'];
  priorityOptions      = [1, 2, 3, 4, 5];
  closeOutcomeOptions  = ['RESOLVED', 'REFUNDED', 'REPLACED', 'REJECTED'];

  // ─── FLATPICKR (stesse impostazioni dei filtri di damage-management) ───
  @ViewChild('detectedAtInput') detectedAtInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dueDateInput')    dueDateInput!:    ElementRef<HTMLInputElement>;
  @ViewChild('openedAtInput')   openedAtInput!:   ElementRef<HTMLInputElement>;
  @ViewChild('closedAtInput')   closedAtInput!:   ElementRef<HTMLInputElement>;
  private pickers = new Map<string, FlatpickrInstance>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private damageService: DamageManagementService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Si arriva qui dalla tabella di /damaged-parcels, spesso scorsa in basso: il router
    // non ripristina lo scroll, quindi la pagina va riportata in cima subito
    // (senza animazione) per mostrare il form dall'inizio.
    window.scrollTo({ top: 0, behavior: 'auto' });

    this.parcelCode = this.route.snapshot.paramMap.get('parcelCode') ?? '';

    this.form = this.fb.group({
      // Riferimenti
      damageParcelCode:    [{ value: this.parcelCode, disabled: true }, [Validators.required]],
      damageParcelId:      [null],
      damageWaybillCode:   [''],
      damageClientCode:    [''],
      damageBrandCode:     [''],
      damageDealerCode:    [''],
      damageCarrierCode:   [''],
      damageTourRoutingId: [null],

      // Classificazione
      damageStatus:   ['OPEN'],
      damageType:     ['', [Validators.required]],
      damageSeverity: ['', [Validators.required]],
      damagePriority: [3],

      // Rilevamento (le date sono Date valorizzate dai flatpickr).
      // Fase e fonte sono obbligatorie per l'endpoint /create.
      damageDetectedPhase:    ['', [Validators.required]],
      damageDetectedAt:       [null],
      damageDetectedLocation: [''],
      damageDetectedBy:       [''],
      damageSource:           ['', [Validators.required]],
      damageLiableParty:      [''],
      damageDescription:      ['', [Validators.required]],

      // Gestione / assegnazione
      damageAssignedTo: [''],
      damageDueDate:    [null],

      // Apertura
      damageOpenedAt: [null],
      damageOpenedBy: [''],

      // Chiusura
      damageClosedAt:     [null],
      damageClosedBy:     [''],
      damageCloseOutcome: [''],
      damageCloseNote:    ['']
    });
  }

  ngAfterViewInit(): void {
    this.initPicker(this.detectedAtInput, 'damageDetectedAt');
    this.initPicker(this.dueDateInput,    'damageDueDate');
    this.initPicker(this.openedAtInput,   'damageOpenedAt');
    this.initPicker(this.closedAtInput,   'damageClosedAt');
  }

  ngOnDestroy(): void {
    this.pickers.forEach(p => p.destroy());
    this.pickers.clear();
  }

  /** Flatpickr con data+ora, come i filtri data di damage-management */
  private initPicker(el: ElementRef<HTMLInputElement>, controlName: string): void {
    if (!el?.nativeElement) { return; }
    this.pickers.set(controlName, flatpickr(el.nativeElement, {
      dateFormat: 'd/m/Y H:i',
      enableTime: true,
      time_24hr: true,
      locale: Italian,
      onChange: (dates: Date[]) => { this.form.get(controlName)?.setValue(dates[0] ?? null); }
    }));
  }

  // ─── NAVIGAZIONE DATE (stessi controlli dei filtri di shipment-management) ───

  /** Sposta la data di `deltaDays` giorni; senza valore parte da oggi */
  adjustDate(controlName: string, deltaDays: number): void {
    const ctrl = this.form.get(controlName);
    const base = ctrl?.value instanceof Date ? new Date(ctrl.value) : this.todayAtMidnight();
    base.setDate(base.getDate() + deltaDays);
    ctrl?.setValue(base);
    this.pickers.get(controlName)?.setDate(base, false);
  }

  /** Imposta la data a oggi (00:00) */
  setDateToday(controlName: string): void {
    const today = this.todayAtMidnight();
    this.form.get(controlName)?.setValue(today);
    this.pickers.get(controlName)?.setDate(today, false);
  }

  /** Svuota la data */
  clearDate(controlName: string): void {
    this.form.get(controlName)?.setValue(null);
    this.pickers.get(controlName)?.clear();
  }

  private todayAtMidnight(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  isFieldInvalid(name: string): boolean {
    const ctrl = this.form.get(name);
    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.warning('Compila i campi obbligatori prima di salvare.');
      return;
    }

    const raw = this.form.getRawValue();
    const damage: Partial<Damage> = {
      ...raw,
      damageParcelId:      raw.damageParcelId != null && raw.damageParcelId !== '' ? Number(raw.damageParcelId) : undefined,
      damageTourRoutingId: raw.damageTourRoutingId != null && raw.damageTourRoutingId !== '' ? Number(raw.damageTourRoutingId) : undefined,
      damagePriority:      raw.damagePriority != null ? Number(raw.damagePriority) : undefined,
      damageDetectedAt:    this.toIso(raw.damageDetectedAt),
      damageDueDate:       this.toIso(raw.damageDueDate),
      damageOpenedAt:      this.toIso(raw.damageOpenedAt),
      damageClosedAt:      this.toIso(raw.damageClosedAt)
    };
    // I campi vuoti non vengono inviati
    Object.keys(damage).forEach(key => {
      const value = (damage as any)[key];
      if (value === '' || value === null || value === undefined) {
        delete (damage as any)[key];
      }
    });

    this.isSaving = true;
    this.damageService.createDamage(damage).pipe(
      finalize(() => { this.isSaving = false; })
    ).subscribe({
      next: () => {
        this.notificationService.success(`Pratica danno aperta per il collo ${this.parcelCode}.`);
        this.router.navigate(['/damage-overview']);
      },
      // 409 (pratica già aperta) e 400 arrivano con il messaggio del backend
      error: (err: Error) => {
        this.notificationService.error(err?.message || 'Errore nella creazione della pratica danno. Riprova più tardi.');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/damage-overview']);
  }

  /** Converte il valore (Date dal flatpickr o stringa) in ISO 8601, o undefined */
  private toIso(value: Date | string | null | undefined): string | undefined {
    if (!value) { return undefined; }
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }
}
